import { Spinner } from '@nextui-org/react-latest'
import CloseIcon from '@public/static/icons/toast/CloseIcon'
import ErrorIcon from '@public/static/icons/toast/ErrorIcon'
import SuccessIcon from '@public/static/icons/toast/SuccessIcon'
import { useEffect, useRef, useState } from 'react'
import * as React from 'react'
import { ToastOptions } from 'react-toastify'

export type ToastMessageProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  closeToast?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toastProps?: any
  icon?: React.ReactNode
  title: string
  message?: string
  type?: 'success' | 'error' | 'pending'
  duration?: number
  options?: ToastOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraContent?: any
}

const bgColors = {
  success: '#E1E5FF',
  pending: '#fbead4',
  error: '#FFD7D7',
}
const solidColors = {
  success: '#A5AFFF',
  pending: '#fec57a',
  error: '#FF8989',
}

const ToastMessage = ({
  closeToast,
  type = 'success',
  title,
  message,
  icon,
  duration = 3,
  extraContent,
}: ToastMessageProps) => {
  const [isHovered, setIsHovered] = useState(false)
  const lastTickTime = useRef<number>(performance.now())
  const progressRef = useRef<HTMLDivElement>(null)
  const remainingTimeRef = useRef<number>(duration)
  const animationFrameId = useRef<number>(null)
  const isHoveredRef = useRef(isHovered)

  useEffect(() => {
    isHoveredRef.current = isHovered
  }, [isHovered])

  const tick = (currentTime: number) => {
    if (isHoveredRef.current) {
      lastTickTime.current = currentTime
      animationFrameId.current = requestAnimationFrame(tick)
      return
    }

    const delta = (currentTime - lastTickTime.current) / 1000
    lastTickTime.current = currentTime

    remainingTimeRef.current = Math.max(0, remainingTimeRef.current - delta)

    if (progressRef.current) {
      const progressPercent = (remainingTimeRef.current / duration) * 100
      progressRef.current.style.width = `${progressPercent}%`
    }

    if (remainingTimeRef.current > 0) {
      animationFrameId.current = requestAnimationFrame(tick)
    } else {
      if (closeToast) closeToast()
    }
  }

  useEffect(() => {
    lastTickTime.current = performance.now()
    remainingTimeRef.current = duration
    animationFrameId.current = requestAnimationFrame(tick)
    return () => {
      if (animationFrameId.current)
        cancelAnimationFrame(animationFrameId.current)
    }
  }, [duration, closeToast])

  const getIcon = () => {
    if (icon) {
      return icon
    }
    if (type === 'error') {
      return <ErrorIcon />
    }
    if (type === 'success') {
      return <SuccessIcon />
    }
  }

  return (
    <div
      className={`max-w-full sm:max-w-[400px] min-w-[300px] p-4 rounded-2xl flex gap-4 mb-3 shadow`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: bgColors[type],
      }}
    >
      {type === 'pending' ? (
        <Spinner size="sm" color="secondary" />
      ) : (
        <div
          className={`p-2.5 flex items-center justify-center aspect-square rounded-2xl max-w-[40px] max-h-[40px]`}
          style={{ backgroundColor: solidColors[type] }}
        >
          {getIcon()}
        </div>
      )}
      <div className="flex-1">
        <div className="font-semiBold text-uguPurple line-clamp-2 break-words">
          {title}
        </div>
        {message && (
          <div className="text-uguPurple font-regular text-xs sm:text-sm line-clamp-2 break-words">
            {message}
          </div>
        )}
        {extraContent ? extraContent : <></>}
        {duration && type !== 'pending' ? (
          <div className="mt-3 bg-uguPurple/5 rounded-full overflow-hidden h-[5px]">
            <div ref={progressRef} className="bg-uguPurple h-full w-full"></div>
          </div>
        ) : (
          <></>
        )}
      </div>
      <div>
        <button onClick={closeToast} className="bg-transparent p-2">
          <CloseIcon />
        </button>
      </div>
    </div>
  )
}

export default ToastMessage
